import { ScoreType } from "src/api/client-api/scoreType/useGetAllScoreType";
import { getPreferenceFromCache, setPreference, deletePreference } from "src/services/PreferenceStore";

const serializeColumnVisibility = (columnVisibility: Map<string, boolean>): string => {
    return JSON.stringify(Array.from(columnVisibility.entries()));
};

export class ColumnsService {
    static saveColumnVisibility = (gridKey: string, columnVisibility: Map<string, boolean>) => {
        const value = Array.from(columnVisibility.entries());
        const cached = getPreferenceFromCache<[string, boolean][]>("grid.columns.visibility", gridKey);
        if (cached !== null && JSON.stringify(cached) === serializeColumnVisibility(columnVisibility)) {
            return;
        }

        setPreference("grid.columns.visibility", gridKey, value);
    };

    static saveColumnOrder = (gridKey: string, columnOrder: string[]) => {
        setPreference("grid.columns.order", gridKey, columnOrder);
    };

    static saveColumnWidth = (gridKey: string, columnWidth: Record<string, number>) => {
        setPreference("grid.columns.width", gridKey, columnWidth);
    };

    static getColumnVisibility = (gridKey: string): Map<string, boolean> => {
        const cached = getPreferenceFromCache<[string, boolean][]>("grid.columns.visibility", gridKey);
        if (cached !== null && Array.isArray(cached)) {
            return new Map(cached);
        }

        // Fallback: read from legacy localStorage key
        const legacyRaw = localStorage.getItem(gridKey);
        if (legacyRaw) {
            try {
                const parsed = JSON.parse(legacyRaw);
                return new Map(parsed);
            } catch {
                /* ignore */
            }
        }

        return new Map<string, boolean>();
    };

    static getColumnOrder = (gridKey: string): string[] => {
        const cached = getPreferenceFromCache<string[]>("grid.columns.order", gridKey);
        if (cached !== null && Array.isArray(cached)) {
            return cached;
        }

        // Fallback: read from legacy localStorage key
        const legacyRaw = localStorage.getItem(`${gridKey}-order`);
        if (legacyRaw) {
            try {
                return JSON.parse(legacyRaw) as string[];
            } catch {
                /* ignore */
            }
        }

        return new Array<string>();
    };

    static getColumnWidth = (gridKey: string): Record<string, number> => {
        const cached = getPreferenceFromCache<Record<string, number>>("grid.columns.width", gridKey);
        if (cached !== null && typeof cached === "object") {
            return cached;
        }

        // Fallback: read from legacy localStorage key
        const legacyRaw = localStorage.getItem(`witdth-${gridKey}`);
        if (legacyRaw) {
            try {
                return JSON.parse(legacyRaw) as Record<string, number>;
            } catch {
                /* ignore */
            }
        }

        return {};
    };

    static deleteColumnVisibility = (gridKey: string) => {
        deletePreference("grid.columns.visibility", gridKey);
        localStorage.removeItem(gridKey);
    };

    static deleteColumnOrder = (gridKey: string) => {
        deletePreference("grid.columns.order", gridKey);
        localStorage.removeItem(`${gridKey}-order`);
    };

    static registerGrid = (gridKey: string) => {
        const currentRegisteredGrid = ColumnsService.getRegisteredGrid();
        if (!currentRegisteredGrid.some((g) => g === gridKey)) {
            currentRegisteredGrid.push(gridKey);
        }

        localStorage.setItem("registered-grid", JSON.stringify(currentRegisteredGrid));
    };

    static getRegisteredGrid = () => {
        const result = localStorage.getItem("registered-grid");

        if (result) {
            const parsedResult = JSON.parse(result);

            if (Array.isArray(parsedResult)) {
                return parsedResult as string[];
            } else {
                console.warn("Invalid parsed result format for registered grid");
            }
        }

        return new Array<string>();
    };

    private static deleteColumnsSettingsOfGridByGridKeyAndColumnsKey = (gridKey: string, columnsKeys: string[]) => {
        const registeredGrid = ColumnsService.getRegisteredGrid();

        if (!registeredGrid.includes(gridKey)) return;

        const currentColumnVisibility = ColumnsService.getColumnVisibility(gridKey);
        const currentColumnOrder = ColumnsService.getColumnOrder(gridKey);

        const currentScoresKeys = Array.from(currentColumnVisibility.keys()).filter((key) => key.includes("score"));
        currentScoresKeys.forEach((key) => currentColumnVisibility.delete(key));

        const keyToRemoveFromColumnOrder = currentScoresKeys.filter((element) => !columnsKeys.includes(element));
        keyToRemoveFromColumnOrder.forEach((key) => {
            const indexToDelete = currentColumnOrder.findIndex((k) => k === key);
            if (indexToDelete !== -1) currentColumnOrder.splice(indexToDelete, 1);
        });

        columnsKeys.forEach((columKey) => currentColumnVisibility.set(columKey, true));

        ColumnsService.saveColumnVisibility(gridKey, currentColumnVisibility);
        ColumnsService.saveColumnOrder(gridKey, currentColumnOrder);
    };

    static updateScoresColumnsSettings = (type: ScoreType["type"], scoreIds: number[]) => {
        mapScoreWithGrid.forEach((gridKey, scoreKey) => {
            if (scoreKey !== type) return;

            const scoresKeys = scoreIds.map((scoreId) => `score-${scoreId}`);
            ColumnsService.deleteColumnsSettingsOfGridByGridKeyAndColumnsKey(gridKey, scoresKeys);
        });
    };

    static initializeTagColumnsVisibility = (gridKey: string, defaultTagVisibility: Record<string, boolean>) => {
        const currentColumnVisibility = ColumnsService.getColumnVisibility(gridKey);
        let changed = false;

        for (const [tagColumnKey, visible] of Object.entries(defaultTagVisibility)) {
            if (!currentColumnVisibility.has(tagColumnKey)) {
                currentColumnVisibility.set(tagColumnKey, visible);
                changed = true;
            }
        }

        if (changed) {
            ColumnsService.saveColumnVisibility(gridKey, currentColumnVisibility);
        }
    };
}

const mapScoreWithGrid = new Map<ScoreType["type"], string>([
    ["product", "product-grid"],
    ["ingredient", "ingredients-grid"],
    ["company", "supplier-grid"],
]);
